import { baseApi } from "../../../../app/api";
import { store } from "../../../../app/store";
import { transformProduct } from "../helpers/productApiHelpers/transformProduct";
import { selectLanguageInfo } from "../../../../common/features/language/languageSelectors";

import type { FetchBaseQueryMeta } from "@reduxjs/toolkit/query";

// Import the RAW types for API responses
import type { RawProduct, RawProductsResponse } from "../../../../types/index";

// Import the final TRANSFORMED types and parameter types
import type {
  GetProductsParams,
  GetProductsResult,
  ProductsCollectionData,
  TransformedProduct,
} from "../types/productApiTypes";

// --- API Slice Definition ---

const WC_STORE_API_BASE =
  window.commonVariables?.wcStoreApiBase || "/wp-json/wc/store/v1";

const MINOR_UNIT_DEFAULT = 2;

export const productApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Fetches products list. Uses RawProductsResponse, transforms to GetProductsResult.
     */
    getProducts: builder.query<GetProductsResult, GetProductsParams>({
      query: (params: GetProductsParams = {}) => {
        const queryParams = new URLSearchParams();
        const multiplier = Math.pow(10, MINOR_UNIT_DEFAULT);

        if (params.category && params.category.length)
          queryParams.append("category", params.category.join(","));
        if (params.tag && params.tag.length)
          queryParams.append("tag", params.tag.join(","));
        if (params.brand && params.brand.length)
          queryParams.append("brand", params.brand.join(","));
        if (params.search) queryParams.append("search", params.search);

        if (params.sku) queryParams.append("sku", params.sku);

        if (params.attributes && Object.keys(params.attributes).length > 0) {
          let i = 0;
          for (const taxonomy in params.attributes) {
            const termSlugs = params.attributes[taxonomy];
            if (termSlugs.length > 0) {
              queryParams.append(`attributes[${i}][attribute]`, taxonomy);
              queryParams.append(`attributes[${i}][slug]`, termSlugs.join(","));
              i++;
            }
          }
          if (Object.keys(params.attributes).length > 1) {
            queryParams.append("attribute_relation", "and");
          }
        }

        if (params.orderby) {
          switch (params.orderby) {
            case "menu_order":
              // Map to the Store API's expectation for menu order
              queryParams.append("orderby", "menu_order");
              queryParams.append("order", "asc");
              break;
            case "popularity":
              queryParams.append("orderby", "popularity");
              break;
            case "date":
              queryParams.append("orderby", "date");
              break;
            case "price-asc":
              queryParams.append("orderby", "price");
              queryParams.append("order", "asc");
              break;
            case "price-desc":
              queryParams.append("orderby", "price");
              queryParams.append("order", "desc");
              break;
            default:
              // Default fallback
              queryParams.append("orderby", "menu_order");
              queryParams.append("order", "asc");
          }
        }
        if (typeof params.minPrice === "number") {
          const minPriceMinor = Math.round(params.minPrice * multiplier);
          queryParams.append("min_price", String(minPriceMinor));
        }
        if (typeof params.maxPrice === "number") {
          const maxPriceMinor = Math.round(params.maxPrice * multiplier);
          queryParams.append("max_price", String(maxPriceMinor));
        }
        if (params.onSale) queryParams.append("on_sale", "true");

        // We include 'onbackorder' so items that are purchasable but not strictly 'instock' are still shown.
        if (params.inStock) {
          queryParams.append("stock_status", "instock,onbackorder");
        }

        if (params.featured) queryParams.append("featured", "true");
        if (params.page) queryParams.append("page", String(params.page));
        if (params.limit) queryParams.append("per_page", String(params.limit));

        return {
          url: `${WC_STORE_API_BASE}/products`,
          params: queryParams,
        };
      },
      transformResponse: (
        response: RawProductsResponse,
        meta: FetchBaseQueryMeta | undefined
      ): GetProductsResult => {
        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getProducts expected an array but received:",
            response
          );
          // Return a valid empty state to prevent downstream errors
          return {
            products: [],
            pagination: { totalProducts: 0, totalPages: 1 },
          };
        }
        const rawProducts: RawProduct[] = response;
        const headers = meta?.response?.headers;
        const totalProducts = parseInt(headers?.get("X-WP-Total") || "0", 10);
        const totalPages = parseInt(headers?.get("X-WP-TotalPages") || "1", 10);

        // --- GATHER DEPENDENCIES HERE ---
        const languageInfo = selectLanguageInfo(store.getState());

        const transformedProducts = rawProducts
          .map((p) => transformProduct(p, languageInfo)) // Pass dependencies
          .filter((p): p is TransformedProduct => p !== null);

        return {
          products: transformedProducts,
          pagination: { totalProducts, totalPages },
        };
      },
      providesTags: (result) =>
        result?.products
          ? [
              ...result.products.map(({ id }) => ({
                type: "Product" as const,
                id,
              })),
              { type: "Product" as const, id: "LIST" },
            ]
          : [{ type: "Product" as const, id: "LIST" }],
    }),

    /**
     * Fetches a list of products by their IDs.
     */
    getProductsByIds: builder.query<TransformedProduct[], (number | string)[]>({
      query: (productIds) => ({
        url: `${WC_STORE_API_BASE}/products`,
        params: {
          include: productIds ? productIds.join(",") : "",
          per_page: productIds.length > 0 ? productIds.length : 0,
        },
      }),
      transformResponse: (
        response: RawProductsResponse
      ): TransformedProduct[] => {
        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getProductsByIds expected an array but received:",
            response
          );
          return [];
        }
        const products: RawProduct[] = response;

        // --- GATHER DEPENDENCIES HERE ---
        const languageInfo = selectLanguageInfo(store.getState());

        return products
          .map((p) => transformProduct(p, languageInfo))
          .filter((p): p is TransformedProduct => p !== null);
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Product" as const, id })),
              { type: "Product" as const, id: "LIST" },
            ]
          : [{ type: "Product" as const, id: "LIST" }],
    }),

    /**
     * Fetches single product by ID. Uses RawProduct, transforms to TransformedProduct.
     */
    getProductById: builder.query<TransformedProduct | null, number | string>({
      query: (id) => ({ url: `${WC_STORE_API_BASE}/products/${id}` }),
      transformResponse: (product: RawProduct): TransformedProduct | null => {
        // --- GATHER DEPENDENCIES HERE ---
        const languageInfo = selectLanguageInfo(store.getState());
        return transformProduct(product, languageInfo); // Pass dependencies
      },
      providesTags: (result, error, id) => [{ type: "Product" as const, id }],
    }),

    /**
     * Fetches single product by Slug. Uses RawProductsResponse, transforms first item.
     */
    getProductBySlug: builder.query<TransformedProduct | null, string>({
      query: (slug) => ({
        url: `${WC_STORE_API_BASE}/products`,
        params: { slug },
      }),
      transformResponse: (
        response: RawProductsResponse
      ): TransformedProduct | null => {
        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getProductBySlug expected an array but received:",
            response
          );
          return null;
        }
        const products: RawProduct[] = response;
        if (products.length === 0) return null;

        // --- GATHER DEPENDENCIES HERE ---
        const languageInfo = selectLanguageInfo(store.getState());
        return transformProduct(products[0], languageInfo); // Pass dependencies
      },
      providesTags: (result) =>
        result ? [{ type: "Product" as const, id: result.id }] : [],
    }),

    /**
     * Fetches variations for a product. Uses RawProductsResponse, transforms to TransformedProduct[].
     */
    getProductVariations: builder.query<TransformedProduct[], number | string>({
      query: (productId) => ({
        url: `${WC_STORE_API_BASE}/products`,
        params: { type: "variation", parent: productId, per_page: 100 },
      }),
      transformResponse: (
        response: RawProductsResponse
      ): TransformedProduct[] => {
        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getProductVariations expected an array but received:",
            response
          );
          return [];
        }
        const variations: RawProduct[] = response;
        const languageInfo = selectLanguageInfo(store.getState());

        return variations
          .map((variation) => transformProduct(variation, languageInfo))
          .filter((v): v is TransformedProduct => v !== null);
      },
      providesTags: (result, error, productId) => [
        { type: "Variation" as const, id: productId },
      ],
    }),

    /**
     * Fetches aggregate data about the product collection, like min/max price.
     */
    getProductsCollectionData: builder.query<
      ProductsCollectionData,
      GetProductsParams
    >({
      query: (params: GetProductsParams = {}) => {
        const queryParams = new URLSearchParams();

        // Always ask the API to calculate the price range.
        queryParams.append("calculate_price_range", "true");

        // Pass along other filters to get a price range relevant to the current view
        if (params.category && params.category.length)
          queryParams.append("category", params.category.join(","));
        if (params.tag && params.tag.length)
          queryParams.append("tag", params.tag.join(","));
        if (params.brand && params.brand.length)
          queryParams.append("brand", params.brand.join(","));
        if (params.search) queryParams.append("search", params.search);
        if (params.inStock) {
          queryParams.append("stock_status", "instock,onbackorder");
        }

        return {
          url: `${WC_STORE_API_BASE}/products/collection-data`,
          params: queryParams,
        };
      },
      transformResponse: (response: {
        price_range: {
          min_price: string;
          max_price: string;
          currency_minor_unit: number;
          currency_symbol: string;
        } | null;
      }): ProductsCollectionData => {
        // Handle the case where no products are found and the API returns null
        if (!response.price_range) {
          return {
            minPrice: 0,
            maxPrice: 1000, // A sensible default max price to prevent errors
            currencySymbol: "₺", // Default fallback symbol
          };
        }

        const currencyMinorUnit = response.price_range.currency_minor_unit ?? 2;
        const divisor = Math.pow(10, currencyMinorUnit);

        // The API now returns min_price and max_price correctly.
        return {
          minPrice: Math.floor(
            parseFloat(response.price_range.min_price) / divisor
          ),
          maxPrice: Math.ceil(
            parseFloat(response.price_range.max_price) / divisor
          ),
          currencySymbol: response.price_range.currency_symbol || "₺", // Pass the symbol through
        };
      },
      providesTags: ["Product"], // This data changes whenever the product list changes
    }),
  }),
  overrideExisting: false,
});

// --- Exports ---
export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductByIdQuery,
  useGetProductBySlugQuery,
  useGetProductsCollectionDataQuery,
  useGetProductVariationsQuery,
  useGetProductsByIdsQuery,
  util: { getRunningQueriesThunk },
} = productApi;

export const {
  getProducts,
  getProductById,
  getProductBySlug,
  getProductsCollectionData,
  getProductVariations,
  getProductsByIds,
} = productApi.endpoints;
